import React, { useRef, useState } from 'react';
import * as d3 from 'd3';
import './TextTimeline.css';
import { ChatTextFill, HospitalFill } from 'react-bootstrap-icons';

const NODE_RADIUS = 20;
const NODE_RADIUS_HOVER = 28;
const TIMELINE_HEIGHT = 120;
const TIMELINE_PADDING = 40;

function TextTimeline({
  messages = [],
  procedureName,
  surgeryDate,
  onHover,
  onClick,
  lockedId,
}) {
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const svgRef = useRef();

  // Group messages by date
  const messagesByDate = messages.reduce((acc, msg) => {
    const date = msg.scheduled_date;
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(msg);
    return acc;
  }, {});
  const formatDateUTC = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    return dt.toLocaleDateString(undefined, { timeZone: 'UTC' });
  };

  // Build timeline items with grouped messages
  const timelineItems = [
    ...(surgeryDate
      ? [
          {
            id: 'surgery',
            text: `${procedureName} scheduled`,
            date: surgeryDate,
            type: 'surgery',
            messages: [],
          },
        ]
      : []),
    ...Object.entries(messagesByDate).map(([date, msgs]) => ({
      date,
      type: 'message',
      messages: msgs,
      id: msgs[0]?.message_id,
    })),
  ].sort((a, b) => new Date(a.date) - new Date(b.date)).filter((item) => item.id != null);
  const width = 700;
  const nodeCount = timelineItems.length;
  const xScale = d3.scaleLinear()
    .domain([0, Math.max(1, nodeCount - 1)])
    .range([TIMELINE_PADDING, width - TIMELINE_PADDING]);

  // Helper to get the absolute position of a node
  const getNodeScreenPosition = (idx) => {
    if (!svgRef.current) return { x: 0, y: 0 };
    const svgRect = svgRef.current.getBoundingClientRect();
    const x = xScale(idx) + svgRect.left;
    const y = svgRect.top + TIMELINE_HEIGHT / 2;
    return { x, y };
  };

  // Handle node hover
  const handleNodeHover = (idx) => {
    setHoveredIdx(idx);
    if (onHover) onHover(timelineItems[idx]);
  };

  // Handle node leave
  const handleNodeLeave = () => {
    setHoveredIdx(null);
    if (onHover) onHover(null);
  };

  const handleNodeClick = (idx) => {
    if (onClick) onClick(timelineItems[idx]);
  };

  return (
    <div style={{ width, overflowX: 'auto', position: 'relative', lineHeight: 0 }}>
      <svg
        ref={svgRef}
        width={width}
        height={TIMELINE_HEIGHT + 12}
        style={{ display: 'block', margin: '0 auto' }}
      >
        {/* Timeline line */}
        <line
          x1={xScale(0)}
          x2={xScale(Math.max(0, nodeCount - 1))}
          y1={TIMELINE_HEIGHT / 2}
          y2={TIMELINE_HEIGHT / 2}
          stroke="#dee2e6"
          strokeWidth={3}
        />

        {/* Timeline nodes */}
        {timelineItems.map((item, idx) => {
          const isHovered = hoveredIdx === idx;
          const isLocked =
            lockedId != null && item.id != null && String(lockedId) === String(item.id);
          const r = isHovered || isLocked ? NODE_RADIUS_HOVER : NODE_RADIUS;
          const fill = item.type === 'surgery' ? '#007bff' : '#6c757d';
          const iconSize = r * 1.6; // slightly smaller than circle

          return (
            <g
              key={item.id != null ? String(item.id) : `node-${idx}`}
              tabIndex={0}
              style={{ cursor: 'pointer', outline: 'none' }}
              onMouseEnter={() => handleNodeHover(idx)}
              onMouseLeave={handleNodeLeave}
              onClick={() => handleNodeClick(idx)}
            >
              {/* Transparent hitbox for easier hover/click */}
              <rect
                x={xScale(idx) - 60}
                y={TIMELINE_HEIGHT / 2 - 60}
                width={120}
                height={120}
                fill="transparent"
                pointerEvents="all"
                style={{ outline: 'none' }}
              />

              {/* White background circle */}
              <circle
                cx={xScale(idx)}
                cy={TIMELINE_HEIGHT / 2}
                r={r}
                fill="#ffffff"
                stroke="white"
                strokeWidth={2}
              />

              {/* Colored icon on top */}
              <g transform={`translate(${xScale(idx) - iconSize / 2}, ${TIMELINE_HEIGHT / 2 - iconSize / 2})`}>
                {item.type === 'surgery' ? (
                  <HospitalFill width={iconSize} height={iconSize} color={fill} />
                ) : (
                  <ChatTextFill width={iconSize} height={iconSize} color={fill} />
                )}
              </g>

              {/* Date label */}
              <text
                x={xScale(idx)}
                y={TIMELINE_HEIGHT / 2 + r + 18}
                textAnchor="middle"
                fontSize="0.85rem"
                fill="#333"
              >
                {formatDateUTC(item.date)}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default TextTimeline; 